/**
 * useDealHasAcceptedProposal - Indica se um negócio possui proposta aceita
 *
 * Quando existe proposta aceita, os produtos/serviços do negócio ficam
 * bloqueados para edição (trava também garantida no banco pelo trigger
 * trg_block_deal_items_when_accepted_proposal). Este hook serve apenas para
 * refletir a regra na interface.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - staleTime zerado + refetch ao focar a janela.
 *            Evita janela de defasagem em que a aba Produtos continua editável
 *            logo após a proposta ser aceita (antes só travava após F5).
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useDealHasAcceptedProposal');

/**
 * Retorna true quando o negócio informado possui ao menos uma proposta aceita.
 */
export function useDealHasAcceptedProposal(dealId: string | null | undefined, enabled = true) {
  const query = useQuery({
    queryKey: ['deal-has-accepted-proposal', dealId],
    enabled: enabled && !!dealId,
    // v1.1.0: sempre revalidar — a trava de edição não pode ficar defasada
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)('deal_has_accepted_proposal', {
        p_deal_id: dealId,
      });
      if (error) {
        logger.warn('Falha ao verificar proposta aceita', { error: error.message });
        return false;
      }
      return !!data;
    },
  });

  return { hasAcceptedProposal: !!query.data, isLoading: query.isLoading };
}
